/**
 * Permission grouping for the Bokapsys staff role editor.
 *
 * Mirrors `features/team/permissionGroups.ts` but scoped to `admin.*`
 * codes seeded by migration 040 (and follow-on migrations that added
 * managed-payouts splits, audit export, etc). Same prefix-longest-wins
 * matcher so a future `admin.managed_payouts.audit` would automatically
 * pick the Managed Payouts bucket without a code change here.
 */

export type AdminPermissionGroup = {
  /** Stable id used in expand/collapse state. */
  id: string
  /** User-facing label rendered as the group header. */
  label: string
  /** One-line helper text shown under the label. */
  description?: string
  /**
   * Code prefixes (or exact codes) that belong in this group, evaluated
   * longest-first so more specific prefixes win over broader ones.
   */
  match: string[]
}

// Order = display order in the staff-role editor.
export const ADMIN_PERMISSION_GROUPS: AdminPermissionGroup[] = [
  {
    id: 'tenants',
    label: 'Customer Tenants',
    description: 'View, create, suspend, activate and delete customer workspaces, plus per-tenant feature flags.',
    match: ['admin.tenants.', 'admin.tenants'],
  },
  {
    id: 'subscriptions',
    label: 'Subscriptions & Billing',
    description: 'Change a tenant’s plan, override billing limits.',
    match: ['admin.subscriptions.'],
  },
  {
    id: 'customer_users',
    label: 'Customer Users',
    description: 'Search and view customer users; deactivate or delete on request.',
    match: ['admin.users.', 'admin.users'],
  },
  {
    id: 'managed_payouts',
    label: 'Managed Payouts',
    description: 'Bokapsys-managed payout dashboard, processing and fee overrides.',
    match: ['admin.managed_payouts.', 'admin.managed_payouts'],
  },
  {
    id: 'impersonation',
    label: 'Impersonation',
    description: 'Impersonate a tenant user for support investigations (auditable).',
    match: ['admin.impersonate'],
  },
  {
    id: 'audit',
    label: 'Audit Log',
    description: 'View and export the cross-tenant audit log.',
    match: ['admin.audit.', 'admin.audit_all'],
  },
  {
    id: 'staff',
    label: 'Bokapsys Staff',
    description: 'Manage Bokapsys staff memberships, invitations and role permissions.',
    match: ['admin.staff.'],
  },
  {
    id: 'api_keys',
    label: 'Platform API Keys',
    description: 'Manage platform-level API keys (cross-tenant integrations).',
    match: ['admin.api_keys'],
  },
  {
    id: 'tools',
    label: 'Dangerous Tools',
    description: 'Raw DB table browser and bulk-delete - grant sparingly.',
    match: ['admin.tools.', 'admin.tools'],
  },
]

const UNGROUPED: AdminPermissionGroup = {
  id: 'other',
  label: 'Other',
  description: 'Permissions not yet categorised.',
  match: [],
}

function matchScore(code: string, prefix: string): number {
  if (prefix === code) return prefix.length + 100
  if (prefix.endsWith('.') && code.startsWith(prefix)) return prefix.length
  if (!prefix.endsWith('.') && code === prefix) return prefix.length + 100
  return 0
}

export function adminGroupForCode(code: string): AdminPermissionGroup {
  let best: { group: AdminPermissionGroup; score: number } | null = null
  for (const g of ADMIN_PERMISSION_GROUPS) {
    for (const p of g.match) {
      const score = matchScore(code, p)
      if (score > 0 && (!best || score > best.score)) {
        best = { group: g, score }
      }
    }
  }
  return best ? best.group : UNGROUPED
}

export type GroupedAdminPermissions = Array<{
  group: AdminPermissionGroup
  permissions: Array<{ code: string; description: string | null }>
}>

export function groupAdminPermissions(
  flat: ReadonlyArray<{ code: string; description: string | null }>,
): GroupedAdminPermissions {
  const buckets = new Map<string, { group: AdminPermissionGroup; permissions: typeof flat[number][] }>()
  for (const p of flat) {
    const g = adminGroupForCode(p.code)
    if (!buckets.has(g.id)) buckets.set(g.id, { group: g, permissions: [] })
    buckets.get(g.id)!.permissions.push(p)
  }
  const ordered: GroupedAdminPermissions = []
  for (const g of ADMIN_PERMISSION_GROUPS) {
    const bucket = buckets.get(g.id)
    if (bucket && bucket.permissions.length > 0) {
      ordered.push({
        group: g,
        permissions: [...bucket.permissions].sort((a, b) => a.code.localeCompare(b.code)),
      })
    }
  }
  const other = buckets.get(UNGROUPED.id)
  if (other && other.permissions.length > 0) {
    ordered.push({
      group: UNGROUPED,
      permissions: [...other.permissions].sort((a, b) => a.code.localeCompare(b.code)),
    })
  }
  return ordered
}
